import { useEffect, useRef, useState, useCallback } from "react";
import utils from "../services/utils";

interface ScrollSpyProps {
  activeShortcutNames: string[];
  sectionRefs: Record<string, React.RefObject<HTMLDivElement>>;
}

/* ==========================================================================
 * CONFIGURATION CONSTANTS (Physics & Tuning Variables)
 * ========================================================================== */

// Horizontal buffer to keep active pills slightly off the edge of the screen
const HORIZONTAL_PILL_BUFFER_PX = 20;

// Buffer for iOS momentum scrolling skipping 0px on snap-back
const TOP_OVERSCROLL_TOLERANCE_PX = 10;

// The maximum pixels the page can move in a single 16ms frame before we
// consider it a DOM layout shift (e.g., adding to cart) rather than a human swipe.
const LAYOUT_SHIFT_VELOCITY_THRESHOLD_PX = 80;

// Momentum Buffer: The continuous downward scroll required to hide the bar
const HIDE_BAR_SCROLL_THRESHOLD_PX = 40;

// Momentum Buffer: The continuous upward scroll required to show the bar
const SHOW_BAR_SCROLL_THRESHOLD_PX = -20;

// Debounce timer to wait for device rotation to finish before measuring DOM
const RESIZE_DEBOUNCE_MS = 150;

// Debounce timer to determine when a programmatic smooth-scroll has concluded
const SCROLL_END_DEBOUNCE_MS = 100;

/* ==========================================================================
 * PLATFORM MITIGATIONS (The "Web Platform Tax")
 * These functions isolate browser-specific quirks away from our business logic.
 * ========================================================================== */

/**
 * MITIGATION: isMobileUrlBarResize
 * Android Chrome Mitigation: Mobile browsers continuously fire `resize` events
 * during vertical scroll as the URL address bar expands/collapses. We only want
 * to trigger expensive DOM layout reads if the user actually rotated their
 * device (horizontal shift).
 */
const isMobileUrlBarResize = (
  currentWidth: number,
  cachedWidth: number
): boolean => {
  return currentWidth === cachedWidth;
};

/**
 * MITIGATION: scrollToCenterPillWithoutMomentumKill
 * iOS "Momentum Kill" (WebKit) Mitigation: Calling `element.scrollIntoView()`
 * natively halts the window's vertical inertia physics. We calculate the exact offset
 * manually and use localized `container.scrollTo()` to preserve the user's vertical swipe momentum.
 */
const scrollToCenterPillWithoutMomentumKill = (
  container: HTMLElement,
  pill: HTMLElement
) => {
  const pillBounds = pill.getBoundingClientRect();
  const containerBounds = container.getBoundingClientRect();

  const isAlreadyVisible =
    pillBounds.left >= containerBounds.left + HORIZONTAL_PILL_BUFFER_PX &&
    pillBounds.right <= containerBounds.right - HORIZONTAL_PILL_BUFFER_PX;

  if (!isAlreadyVisible) {
    const currentScrollLeft = container.scrollLeft;
    const pillOffsetLeft = pillBounds.left - containerBounds.left;

    const targetLeft =
      currentScrollLeft +
      pillOffsetLeft -
      containerBounds.width / 2 +
      pillBounds.width / 2;

    container.scrollTo({
      left: targetLeft,
      behavior: "smooth",
    });
  }
};

/**
 * MITIGATION: resolveScrollIntent
 * Phantom Scroll & Layout Shift Mitigation: Combines a Momentum Buffer (Thresholding)
 * and Velocity Caps to ignore sudden DOM layout shifts (like adding to cart),
 * programmatic auto-scrolls, and iOS rubber-banding bounce backs.
 */
const resolveScrollIntent = (
  currentY: number,
  previousY: number,
  currentScrollMomentum: number,
  isCurrentlyVisible: boolean
) => {
  const deltaY = currentY - previousY;
  const isAtTop = currentY <= TOP_OVERSCROLL_TOLERANCE_PX;
  let updatedScrollMomentum = currentScrollMomentum;

  // 1. Overscroll / Top-of-page safety: Never hide bar at the top
  if (isAtTop) {
    return {
      shouldHideBar: false,
      updatedScrollMomentum: 0,
      clampedY: Math.max(0, currentY),
    };
  }

  // 2. Velocity Cap (Layout Shift / Programmatic Jump Protection)
  // If the page jumps by more than the threshold in a single frame, it is a programmatic
  // event (auto-scroll or DOM injection), not a human swipe. Ignore it.
  if (Math.abs(deltaY) > LAYOUT_SHIFT_VELOCITY_THRESHOLD_PX) {
    return {
      shouldHideBar: !isCurrentlyVisible, // Keep existing state
      updatedScrollMomentum: 0, // Reset accumulator
      clampedY: Math.max(0, currentY),
    };
  }

  // 3. Momentum Buffer (Anti-Micro-Jitter)
  // We require a continuous, intentional swipe to trigger a UI change.
  // This "deadband" absorbs thumb wiggles and minor scroll bounces.
  if (deltaY > 0) {
    if (updatedScrollMomentum < 0) updatedScrollMomentum = 0; // Reset upward momentum
    updatedScrollMomentum += deltaY;
  } else if (deltaY < 0) {
    if (updatedScrollMomentum > 0) updatedScrollMomentum = 0; // Reset downward momentum
    updatedScrollMomentum += deltaY;
  }

  let shouldHideBar = !isCurrentlyVisible; // Default to maintaining current state

  // Require intentional continuous scrolling to trigger visibility changes
  if (updatedScrollMomentum > HIDE_BAR_SCROLL_THRESHOLD_PX) {
    shouldHideBar = true;
  } else if (updatedScrollMomentum < SHOW_BAR_SCROLL_THRESHOLD_PX) {
    shouldHideBar = false;
  }

  return {
    shouldHideBar,
    updatedScrollMomentum,
    clampedY: Math.max(0, currentY),
  };
};

/* ==========================================================================
 * MAIN HOOK: useMenuScrollSpy
 * ========================================================================== */

/**
 * # useMenuScrollSpy
 * High-performance browser orchestration hook for the ShortcutBar.
 * * ### CORE ARCHITECTURE & PERFORMANCE STRATEGY
 * 1. **Decomposed Logic:** Separates viewport visibility from state synchronization to maintain < 5 Cognitive Complexity.
 * 2. **Direct DOM Mutation:** Bypasses React's render cycle for 60fps transform/opacity transitions.
 * 3. **The "Silent Sync":** Preserves momentum by synchronizing horizontal scroll even while the bar is visually hidden.
 * 4. **Hybrid Settle Logic:** Combines RAF (requestAnimationFrame) throttling with a debounce to smoothly handle programmatic scroll termination.
 * 5. **Metric Caching:** Eliminates layout thrashing by reading expensive DOM dimensions only on mount or legitimate axis-shifts.
 */
export const useMenuScrollSpy = ({
  activeShortcutNames,
  sectionRefs,
}: ScrollSpyProps) => {
  const [activeCategoryName, setActiveCategoryName] = useState<string | null>(
    null
  );

  // Element Refs
  const categoryNavScrollRef = useRef<HTMLDivElement | null>(null);
  const categoryPillRefs = useRef<Record<string, HTMLElement | null>>({});
  const stickyBarWrapperRef = useRef<HTMLDivElement>(null);

  // Coordination Refs
  const previousViewportScrollY = useRef(0);
  const isBarVisibleRef = useRef<boolean>(true);
  const isScrollFrameProcessing = useRef(false);
  const autoScrollTargetRef = useRef<string | null>(null);
  const scrollEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const layoutMetricsRef = useRef({ stickyOffset: 0, probeY: 0 });
  const cachedViewportWidthRef = useRef(0);
  const resizeDebounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const accumulatedScrollMomentumRef = useRef(0);
  const hasUserInteractedRef = useRef(false);

  // --- 1. LEAF FUNCTIONS (Pure helpers) ---

  /**
   * ACTION: findSectionAtLine
   * Maps vertical Y-coordinates to the corresponding menu section Ref.
   * Defined early in the lexical scope to avoid TDZ (Temporal Dead Zone) errors.
   */
  const findSectionAtLine = useCallback(
    (detectionThresholdY: number): string | null => {
      return (
        activeShortcutNames.find((shortcutName) => {
          const sectionElement = sectionRefs[shortcutName]?.current;
          if (!utils.isDefined(sectionElement)) return false;
          const { top, bottom } = sectionElement.getBoundingClientRect();
          return top <= detectionThresholdY && bottom > detectionThresholdY;
        }) ?? null
      );
    },
    [activeShortcutNames, sectionRefs]
  );

  /**
   * ACTION: scrollActivePillIntoView
   * Centers the target pill horizontally within the navigation track.
   * Delegates to our iOS Momentum Mitigator function.
   */
  const scrollActivePillIntoView = useCallback((activeCategoryName: string) => {
    const container = categoryNavScrollRef.current;
    const pill = categoryPillRefs.current[activeCategoryName];

    if (!utils.isDefined(container) || !utils.isDefined(pill)) return;

    // Apply the WebKit mitigation
    scrollToCenterPillWithoutMomentumKill(container, pill);
  }, []);

  // --- 2. ORCHESTRATORS (Logic Gates) ---

  /**
   * ACTION: updateVisibility
   * Hardware-accelerated toggle for the nav bar.
   * Uses direct DOM manipulation to slide the bar out of view,
   * and restores it on scroll-up.
   */
  const updateVisibility = useCallback(
    (shouldHideBar: boolean, isManual: boolean) => {
      const navElement = stickyBarWrapperRef.current;
      if (!navElement || !isManual) return;

      if (shouldHideBar) {
        navElement.style.transform = `translateY(-100%)`;
        navElement.style.opacity = "0";
        isBarVisibleRef.current = false;
      } else {
        navElement.style.transform = `translateY(0px)`;
        navElement.style.opacity = "1";

        // Anti-stale Sync: restore horizontal alignment if bar was hidden
        if (!isBarVisibleRef.current && activeCategoryName) {
          scrollActivePillIntoView(activeCategoryName);
        }
        isBarVisibleRef.current = true;
      }
    },
    [activeCategoryName, scrollActivePillIntoView]
  );

  /**
   * ACTION: syncActiveState
   * Reconciles React state with the physical scroll position.
   * Prioritizes the 'Auto-Scroll' target to prevent state flickering during smooth-scroll jumps.
   */
  const syncActiveState = useCallback(() => {
    const detected = findSectionAtLine(layoutMetricsRef.current.probeY);
    const target = autoScrollTargetRef.current;

    if (utils.isDefined(target)) {
      if (activeCategoryName !== target) setActiveCategoryName(target);
      return;
    }

    if (utils.isDefined(detected) && detected !== activeCategoryName) {
      setActiveCategoryName(detected);
    }
  }, [activeCategoryName, findSectionAtLine]);

  /**
   * ACTION: scrollToSection
   * High-level entry point for shortcut clicks.
   * Instantly locks the Scroll-Spy state, force-shows the bar, and clears
   * stale 'unlock' timers from previous rapid-fire clicks.
   */
  const scrollToSection = useCallback(
    (targetSectionId: string) => {
      const targetElement = sectionRefs[targetSectionId]?.current;
      const navElement = stickyBarWrapperRef.current;
      if (!utils.isDefined(targetElement) || !utils.isDefined(navElement))
        return;

      if (scrollEndTimeoutRef.current)
        clearTimeout(scrollEndTimeoutRef.current);

      autoScrollTargetRef.current = targetSectionId;
      setActiveCategoryName(targetSectionId);
      scrollActivePillIntoView(targetSectionId);

      navElement.style.transform = `translateY(0px)`;
      navElement.style.opacity = "1";

      const absoluteTargetY =
        targetElement.getBoundingClientRect().top +
        window.pageYOffset -
        layoutMetricsRef.current.stickyOffset;
      window.scrollTo({ top: absoluteTargetY, behavior: "smooth" });
    },
    [sectionRefs, scrollActivePillIntoView]
  );

  // --- 3. EFFECTS (Event Pipeline) ---

  /**
   * EFFECT: Interaction Lock (First-Load Protection)
   * Prevents programmatic smooth-scrolls (like initial page load jumps)
   * from hiding the bar before the user has actually touched the screen.
   */
  useEffect(() => {
    const unlockIntent = () => {
      hasUserInteractedRef.current = true;
    };
    // { once: true } auto-removes the listener after it fires, keeping memory clean
    window.addEventListener("touchstart", unlockIntent, {
      passive: true,
      once: true,
    });
    window.addEventListener("wheel", unlockIntent, {
      passive: true,
      once: true,
    });
    window.addEventListener("mousedown", unlockIntent, {
      passive: true,
      once: true,
    });
  }, []);

  /**
   * EFFECT: Dynamic Layout Caching & Orientation Observer
   * Calculates and caches the physical boundaries of the sticky navigation.
   */
  useEffect(() => {
    // Prime the cache with the starting width
    cachedViewportWidthRef.current = window.innerWidth;

    const syncLayoutMetrics = () => {
      const navElement = stickyBarWrapperRef.current;
      if (!utils.isDefined(navElement)) return;

      // WARNING: These are forced synchronous layout reads (Expensive)
      const barHeight = navElement.offsetHeight;
      const computedTop =
        parseInt(window.getComputedStyle(navElement).top, 10) || 0;
      const dynamicProbeY = computedTop + barHeight;

      layoutMetricsRef.current = {
        stickyOffset: dynamicProbeY,
        probeY: dynamicProbeY,
      };
    };

    const handleResize = () => {
      const currentWidth = window.innerWidth;

      // Leverage mitigation check to ignore vertical URL bar resizes
      if (isMobileUrlBarResize(currentWidth, cachedViewportWidthRef.current))
        return;

      cachedViewportWidthRef.current = currentWidth;
      clearTimeout(resizeDebounceTimerRef.current);

      resizeDebounceTimerRef.current = setTimeout(() => {
        requestAnimationFrame(syncLayoutMetrics);
      }, RESIZE_DEBOUNCE_MS);
    };

    syncLayoutMetrics();
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeDebounceTimerRef.current);
    };
  }, []);

  /**
   * EFFECT: Initial UI Reset
   * Forces the horizontal pill container to the baseline to clear any stale cache state.
   */
  useEffect(() => {
    const scrollContainer = categoryNavScrollRef.current;
    if (!utils.isDefined(scrollContainer)) return;
    requestAnimationFrame(() => {
      scrollContainer.scrollLeft = 0;
      scrollContainer.scrollTo?.({ left: 0, behavior: "auto" });
    });
  }, []);

  /**
   * EFFECT: Main Scroll Orchestrator
   * The primary event loop. Combines RAF throttling for performance with
   * debounced settle-logic to determine when a programmatic jump has ended.
   */
  useEffect(() => {
    previousViewportScrollY.current = window.pageYOffset;
    const interrupt = () => {
      autoScrollTargetRef.current = null;
    };

    const handleScroll = () => {
      if (scrollEndTimeoutRef.current)
        clearTimeout(scrollEndTimeoutRef.current);
      scrollEndTimeoutRef.current = setTimeout(() => {
        autoScrollTargetRef.current = null;
      }, SCROLL_END_DEBOUNCE_MS);

      if (isScrollFrameProcessing.current) return;
      isScrollFrameProcessing.current = true;

      requestAnimationFrame(() => {
        isScrollFrameProcessing.current = false;
        const currentY = window.pageYOffset;

        // Apply our Physics Mitigations for Intent
        const { shouldHideBar, updatedScrollMomentum, clampedY } =
          resolveScrollIntent(
            currentY,
            previousViewportScrollY.current,
            accumulatedScrollMomentumRef.current,
            isBarVisibleRef.current
          );

        accumulatedScrollMomentumRef.current = updatedScrollMomentum;
        previousViewportScrollY.current = clampedY;

        // Only allow the bar to hide if the user has physically initiated an action
        const finalShouldHide = hasUserInteractedRef.current
          ? shouldHideBar
          : false;
        const isManual =
          currentY >= 0 && !utils.isDefined(autoScrollTargetRef.current);

        updateVisibility(finalShouldHide, isManual);
        syncActiveState();
      });
    };

    document.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("wheel", interrupt, { passive: true });
    document.addEventListener("touchstart", interrupt, { passive: true });

    return () => {
      document.removeEventListener("scroll", handleScroll);
      document.removeEventListener("wheel", interrupt);
      document.removeEventListener("touchstart", interrupt);
    };
  }, [activeCategoryName, syncActiveState, updateVisibility]);

  /**
   * EFFECT: Horizontal Synchronization
   * Bridges vertical position to horizontal feedback.
   * Uses "Silent Sync" to preserve momentum while the bar is out of viewport.
   */
  useEffect(() => {
    if (
      !activeCategoryName ||
      autoScrollTargetRef.current ||
      !isBarVisibleRef.current
    )
      return;
    requestAnimationFrame(() => scrollActivePillIntoView(activeCategoryName));
  }, [activeCategoryName, scrollActivePillIntoView]);

  return {
    activeCategoryName,
    stickyBarWrapperRef,
    categoryNavScrollRef,
    categoryPillRefs,
    scrollToSection,
  };
};
